/**
 * Centralized query keys for React Query cache management
 * Ensures consistent cache invalidation across the app
 */

export const QUERY_KEYS = {
  // Challenges
  CHALLENGES: 'challenges',
  TEAM_CHALLENGES: 'team-challenges',
  INTERNAL_CHALLENGES: 'internal-challenges',
  GAMEPASS_CHALLENGES: 'gamepass-challenges',
  CHALLENGE_TEMPLATES: 'challenge-templates',
  CHALLENGE_LAUNCHES: 'challenge-launches',
  
  // User related
  USER_PROFILE: 'user-profile',
  USER_GOALS: 'user-goals',
  USER_INVENTORY: 'user-inventory',
  CURRENT_EMPLOYEE: 'current-employee',
  EMPLOYEES: 'employees',
  
  // Organizations
  ORGANIZATIONS: 'organizations',
  // ⚠️ GROUP_ORGANIZATIONS appartient au SEUL hook useGroupOrganizations, dont le
  // queryFn renvoie un OBJET { organizations, totalEmployees, ... }.
  // Toute page qui veut juste la liste [{id, name}] DOIT prendre sa propre clé :
  // React Query n'entretient qu'une entrée de cache par clé, donc deux queryFn de
  // formes différentes sous la même clé se écrasent l'une l'autre. C'est ce qui
  // faisait planter /manager/group-claims (« K?.map is not a function », 29/07/2026) :
  // ManagerLayout monte la query « objet » sur toute la branche /manager, la page
  // enfant héritait de l'objet et appelait .map dessus.
  GROUP_ORGANIZATIONS: 'group-organizations',
  GROUP_CLAIMS_ORGANIZATIONS: 'group-claims-organizations',
  CHALLENGE_VISIBILITY_ORGANIZATIONS: 'challenge-visibility-organizations',
  
  // Rewards
  REWARDS: 'rewards',
  SHOP_REWARDS: 'shop-rewards',
  GAMEPASS_REWARDS: 'gamepass-rewards',
  LIBPASS_SHOP_VISIBLE: 'libpass-shop-visible',
  
  // Gamification Products
  GAMIFICATION_PRODUCTS_MANAGER: 'gamification-products-manager',
  GAMIFICATION_PRODUCTS_ADMIN: 'gamification-products-admin',
  GAMIFICATION_VISIBILITY: 'gamification-visibility',
  
  // Participations
  PARTICIPATIONS: 'participations',
  CHALLENGE_PARTICIPATIONS: 'challenge-participations',
  CHALLENGE_PARTICIPANTS_COUNT: 'challenge-participants-count',
  
  // Admin
  ALL_MANAGER_TEMPLATES: 'all-manager-templates',
  SYSTEM_HEALTH: 'system-health',
  DEVICE_HISTORY: 'device-history',
  ALL_ORG_FEATURE_FLAGS: 'all-org-feature-flags',
  
  // Manager
  MANAGER_CHALLENGE_MONITOR: 'manager-challenge-monitor',
  ORG_FEATURE_FLAGS: 'org-feature-flags',
  ORG_EMAIL_TEMPLATES: 'org-email-templates',
  MANAGER_GOALS_LIBRARY: 'manager-goals-library',
  MY_COMPANY_EVENTS: 'my-company-events',

  // ===== Lot 5a — Tier A =====
  ADMIN_CHALLENGE_METRICS: 'admin-challenge-metrics',
  EMAIL_TEMPLATES: 'email-templates',
  LEADERBOARD_HISTORY: 'leaderboard-history',
  LEADERBOARD_AVAILABLE_MONTHS: 'leaderboard-available-months',
  READING_BOOKS: 'reading-books',
  GOAL_BOOK_PREFERENCES: 'goal-book-preferences',
  MOBILITY_WEEKLY_CHALLENGES: 'mobility-weekly-challenges',
  GROUP_HIERARCHY: 'group-hierarchy',
  SYSTEM_ALERT_CONFIG: 'system-alert-config',
  SYSTEM_ALERT_STATS: 'system-alert-stats',
  MONTHLY_RANKINGS_HISTORY: 'monthly-rankings-history',
  VALIDATION_BLOCKS: 'validation-blocks',
  USER_SEARCH: 'user-search',

  // ===== Lot 5b — Tier B =====
  PENDING_CARPOOLING_REQUESTS: 'pending-carpooling-requests',
  CARPOOLING_CONFIRMATIONS: 'carpooling-confirmations',
  PEER_VALIDATION_REQUESTS: 'peer-validation-requests',
  USER_ORG_MANAGEMENT: 'user-org-management',

  // ===== Lot 5c — Tier C decomposition =====
  ADMIN_USERS: 'admin-users',
  DIGITAL_TICKET_POOLS: 'digital-ticket-pools',
  DIGITAL_TICKETS_LIST: 'digital-tickets-list',
  PENDING_TICKETS: 'pending-tickets',

  // ===== Credits / Pool =====
  ORGANIZATION_CREDITS: 'organization-credits',
  POOL_BALANCE_FOR_DOTATION: 'pool-balance-for-dotation',
  POOL_TOPUP_HISTORY: 'pool-topup-history',

  // ===== Commute / Mobility =====
  COMMUTE_IMPACT_STATS: 'commute-impact-stats',
  COMMUTE_STREAK_DATES: 'commute-streak-dates',
  WEEKLY_COMMUTE_STATS: 'weekly-commute-stats',
  MONTHLY_COMMUTE_COUNTS: 'monthly-commute-counts',
  ORG_COMMUTE_STATS: 'org-commute-stats',

  // ===== Lot S — Wallet / diamants =====
  USER_WALLET: 'user-wallet',                // ← solde diamants du badge (useKokenWallet)
  CLAIMED_REWARDS: 'claimed-rewards',        // ← liste des récompenses réclamées
  WALLET_TRANSACTIONS: 'wallet-transactions',
  WALLET_TRANSACTIONS_STATS: 'wallet-transactions-stats',
  EMPLOYEE_CREDITS: 'employee-credits',
  ORG_EMPLOYEE_CREDITS: 'org-employee-credits',
  

  // ===== Leaderboard V3 (intra/inter/dual) =====
  LEADERBOARD_V3_INTRA: 'leaderboard-v3-intra',
  LEADERBOARD_V3_INTER: 'leaderboard-v3-inter',
  LEADERBOARD_V3_DUAL_RANK: 'leaderboard-v3-dual-rank',
} as const;

export type QueryKey = typeof QUERY_KEYS[keyof typeof QUERY_KEYS];

/**
 * Lot T — Standardized cache config for ALL wallet queries.
 * Realtime sync (Lot S.3) handles immediate push on DB events; these values
 * act as a safety net for offline/background scenarios.
 */
export const WALLET_CACHE = {
  staleTime: 60 * 1000,            // 1 min — short freshness window
  gcTime: 5 * 60 * 1000,           // 5 min — keep in memory while user navigates
  refetchInterval: 5 * 60 * 1000,  // safety net if Realtime is dropped
  refetchOnWindowFocus: true,
} as const;
